// src/components/GenerationStatusWidget.tsx
// Floating widget showing background generation status

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  ChevronDown,
  ChevronUp,
  Minimize2,
  Maximize2,
  CheckCircle,
  AlertCircle,
  XCircle,
  Loader2,
  Sparkles,
  Trash2,
  ExternalLink,
  FileText,
} from 'lucide-react';
import { useBackgroundGeneration, GenerationTask } from '../contexts/BackgroundGenerationContext';

// ============================================================
// TASK CARD COMPONENT
// ============================================================

function TaskCard({ task, onCancel, onRemove, onView }: {
  task: GenerationTask;
  onCancel: () => void;
  onRemove: () => void;
  onView: () => void;
}) {
  const isRunning = task.status === 'pending' || task.status === 'analyzing' || task.status === 'generating';
  const isComplete = task.status === 'complete';
  const isError = task.status === 'error';
  const isCancelled = task.status === 'cancelled';

  const statusColors = {
    pending: 'text-bw-muted',
    analyzing: 'text-bw-primary',
    generating: 'text-bw-primary',
    complete: 'text-success',
    error: 'text-urgent',
    cancelled: 'text-bw-muted',
  };

  const statusIcons = {
    pending: <Loader2 className="h-4 w-4 animate-spin" />,
    analyzing: <Loader2 className="h-4 w-4 animate-spin" />,
    generating: <Sparkles className="h-4 w-4 animate-pulse" />,
    complete: <CheckCircle className="h-4 w-4" />,
    error: <AlertCircle className="h-4 w-4" />,
    cancelled: <XCircle className="h-4 w-4" />,
  };

  const statusLabels = {
    pending: 'Starting...',
    analyzing: 'Analyzing',
    generating: 'Generating',
    complete: 'Complete',
    error: 'Failed',
    cancelled: 'Cancelled',
  };

  return (
    <div className={`p-3 rounded-lg border ${
      isRunning ? 'bg-bw-surface border-bw-border' :
      isComplete ? 'bg-success-bg border-success-border' :
      isError ? 'bg-urgent-bg border-urgent-border' :
      'bg-bw-surface-subtle border-bw-border'
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-bw-ink truncate" title={task.contractTitle}>
            {task.contractTitle.length > 35
              ? task.contractTitle.substring(0, 35) + '...'
              : task.contractTitle}
          </p>
          <div className={`flex items-center gap-1.5 text-xs ${statusColors[task.status]}`}>
            {statusIcons[task.status]}
            <span>{statusLabels[task.status]}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {isRunning && (
            <button
              onClick={onCancel}
              className="p-1 text-bw-muted hover:text-urgent hover:bg-urgent-bg rounded transition-colors"
              title="Cancel"
            >
              <XCircle className="h-4 w-4" />
            </button>
          )}
          {!isRunning && (
            <button
              onClick={onRemove}
              className="p-1 text-bw-muted hover:text-bw-muted hover:bg-bw-surface-subtle rounded transition-colors"
              title="Remove"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Progress Bar (when running) */}
      {isRunning && (
        <div className="mb-2">
          <div className="flex items-center justify-between text-xs text-bw-muted mb-1">
            <span className="truncate">{task.currentStep}</span>
            <span className="font-medium ml-2">{Math.round(task.progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-bw-primary-tint0 h-full rounded-full transition-all duration-300 ease-out"
              style={{ width: `${task.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Metrics (when running or complete) */}
      {(isRunning || isComplete) && (task.evidenceCount || task.requirementCount || task.matchedCount) && (
        <div className="flex gap-3 text-xs text-bw-muted mb-2">
          {task.evidenceCount !== undefined && (
            <span>{task.evidenceCount} evidence</span>
          )}
          {task.requirementCount !== undefined && (
            <span>{task.requirementCount} reqs</span>
          )}
          {task.matchedCount !== undefined && (
            <span>{task.matchedCount} matched</span>
          )}
        </div>
      )}

      {/* Error message */}
      {isError && task.error && (
        <p className="text-xs text-urgent mb-2 line-clamp-2">{task.error}</p>
      )}

      {/* View button (when complete) */}
      {isComplete && (
        <button
          onClick={onView}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-success text-white text-xs font-medium rounded-md hover:bg-emerald-700 transition-colors"
        >
          <FileText className="h-3.5 w-3.5" />
          View Proposal
          <ExternalLink className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

// ============================================================
// MAIN WIDGET COMPONENT
// ============================================================

export function GenerationStatusWidget() {
  const navigate = useNavigate();
  const {
    tasks,
    isWidgetExpanded,
    isWidgetMinimized,
    cancelGeneration,
    removeTask,
    clearCompleted,
    toggleWidgetExpanded,
    toggleWidgetMinimized,
  } = useBackgroundGeneration();

  // Don't render if no tasks
  if (tasks.length === 0) {
    return null;
  }

  const runningCount = tasks.filter(t =>
    t.status === 'pending' || t.status === 'analyzing' || t.status === 'generating'
  ).length;

  const completedCount = tasks.filter(t =>
    t.status === 'complete' || t.status === 'error' || t.status === 'cancelled'
  ).length;

  // Calculate overall progress for minimized view
  const overallProgress = runningCount > 0
    ? Math.round(
        tasks
          .filter(t => t.status === 'pending' || t.status === 'analyzing' || t.status === 'generating')
          .reduce((acc, t) => acc + t.progress, 0) / runningCount
      )
    : 100;

  const handleViewProposal = (task: GenerationTask) => {
    if (task.contractId) {
      navigate(`/workspace/${task.contractId}`);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 font-sans">
      {/* Minimized View */}
      {isWidgetMinimized ? (
        <button
          onClick={toggleWidgetMinimized}
          className="flex items-center gap-2 px-4 py-2 bg-bw-surface rounded-full shadow-lg border border-bw-border hover:shadow-xl transition-shadow"
        >
          <div className="relative">
            <Sparkles className="h-5 w-5 text-bw-primary" />
            {runningCount > 0 && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-bw-primary-tint0 rounded-full animate-pulse" />
            )}
          </div>
          <span className="text-sm font-medium text-bw-ink">
            {runningCount > 0 ? `${runningCount} generating` : `${completedCount} complete`}
          </span>
          {runningCount > 0 && (
            <div className="w-16 bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div
                className="bg-bw-primary-tint0 h-full rounded-full transition-all"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          )}
          <Maximize2 className="h-4 w-4 text-bw-muted" />
        </button>
      ) : (
        /* Expanded Widget */
        <div className="w-80 bg-bw-surface rounded-xl shadow-2xl border border-bw-border overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-bw-sidebar text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <span className="font-medium text-sm">Proposal Generation</span>
              {runningCount > 0 && (
                <span className="px-1.5 py-0.5 bg-white/20 rounded text-xs">
                  {runningCount} active
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={toggleWidgetExpanded}
                className="p-1 hover:bg-white/10 rounded transition-colors"
                title={isWidgetExpanded ? 'Collapse' : 'Expand'}
              >
                {isWidgetExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </button>
              <button
                onClick={toggleWidgetMinimized}
                className="p-1 hover:bg-white/10 rounded transition-colors"
                title="Minimize"
              >
                <Minimize2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Task List (when expanded) */}
          {isWidgetExpanded && (
            <div className="max-h-96 overflow-y-auto">
              <div className="p-3 space-y-2">
                {tasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onCancel={() => cancelGeneration(task.id)}
                    onRemove={() => removeTask(task.id)}
                    onView={() => handleViewProposal(task)}
                  />
                ))}
              </div>

              {/* Clear completed button */}
              {completedCount > 0 && (
                <div className="px-3 pb-3">
                  <button
                    onClick={clearCompleted}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-bw-muted hover:text-bw-ink hover:bg-bw-surface-subtle rounded-lg transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Clear {completedCount} finished
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Collapsed Summary */}
          {!isWidgetExpanded && (
            <div className="px-4 py-3 border-t border-bw-border">
              <div className="flex items-center justify-between text-sm">
                <span className="text-bw-muted">
                  {runningCount > 0
                    ? `${runningCount} generating, ${completedCount} done`
                    : `${completedCount} completed`}
                </span>
                {runningCount > 0 && (
                  <span className="font-medium text-bw-primary">{overallProgress}%</span>
                )}
              </div>
              {runningCount > 0 && (
                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="bg-bw-primary-tint0 h-full rounded-full transition-all"
                    style={{ width: `${overallProgress}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
